if (!customElements.get('article-toc')) {
  customElements.define(
    'article-toc',
    class ArticleToc extends HTMLElement {
      // Headings rendered by shortcodes (product/collection cards) are not part of the post outline.
      ignoredSelector = '.article-inline-product, .article-inline-collection-horizontal, .article-audio';

      connectedCallback() {
        this.list = this.querySelector('[data-toc-list]');
        this.content = this.closest('.article__wrapper')?.querySelector('.article__content');
        if (!this.list || !this.content) return;

        const levels = (this.dataset.levels || 'h2,h3').split(',');
        const minHeadings = parseInt(this.dataset.minHeadings, 10) || 2;
        this.headings = Array.from(this.content.querySelectorAll(levels.join(','))).filter(
          (heading) => heading.textContent.trim() && !heading.closest(this.ignoredSelector)
        );

        if (this.headings.length < minHeadings) {
          this.hidden = true;
          return;
        }

        this.render();
        this.hidden = false;
        this.list.addEventListener('click', this.onClick.bind(this));
        this.observe();

        // On desktop the TOC sits in a sidebar beside the content, where it should always be expanded.
        this.nav = this.querySelector('.article-toc__nav');
        this.sidebarQuery = window.matchMedia('(min-width: 1024px)');
        this.onSidebarChange = this.syncSidebar.bind(this);
        this.sidebarQuery.addEventListener('change', this.onSidebarChange);
        this.syncSidebar();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.sidebarQuery?.removeEventListener('change', this.onSidebarChange);
      }

      get isSidebar() {
        return this.sidebarQuery?.matches && this.nextElementSibling?.classList.contains('article__content');
      }

      syncSidebar() {
        const details = this.querySelector('details');
        if (details && this.isSidebar) details.open = true;
      }

      render() {
        const usedIds = new Set(Array.from(document.querySelectorAll('[id]'), (el) => el.id));
        const topLevel = Math.min(...this.headings.map((heading) => this.getLevel(heading)));
        // Each stack entry is a list and the heading level of the items it holds.
        const stack = [{ level: topLevel, list: this.list }];
        this.links = new Map();
        this.list.innerHTML = '';

        this.headings.forEach((heading) => {
          if (!heading.id) heading.id = this.uniqueId(this.slugify(heading.textContent), usedIds);

          const level = this.getLevel(heading);
          while (stack.length > 1 && level < stack[stack.length - 1].level) stack.pop();

          const parent = stack[stack.length - 1];
          // Deeper heading: nest it under the previous item (also when a level is skipped, e.g. h2 → h4).
          if (level > parent.level && parent.list.lastElementChild) {
            const sublist = document.createElement('ol');
            sublist.className = 'article-toc__list article-toc__list--nested';
            parent.list.lastElementChild.appendChild(sublist);
            stack.push({ level, list: sublist });
          }

          const item = document.createElement('li');
          item.className = 'article-toc__item';
          const link = document.createElement('a');
          link.className = 'article-toc__link';
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          item.appendChild(link);
          stack[stack.length - 1].list.appendChild(item);
          this.links.set(heading, link);
        });
      }

      onClick(event) {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;

        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        if (!target) return;

        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.replaceState(null, '', link.hash);
        this.setActive(target);
      }

      observe() {
        this.visible = new Set();
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.visible.add(entry.target);
              else this.visible.delete(entry.target);
            });
            const current = this.headings.find((heading) => this.visible.has(heading));
            if (current) this.setActive(current);
          },
          { rootMargin: '0px 0px -60% 0px' }
        );
        this.headings.forEach((heading) => this.observer.observe(heading));
      }

      setActive(heading) {
        this.links.forEach((link, linkHeading) => {
          if (linkHeading === heading) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        if (this.isSidebar) this.revealLink(this.links.get(heading));
      }

      // Keep the active link visible inside the sidebar's own scroll area without scrolling the page.
      revealLink(link) {
        if (!link || !this.nav || this.nav.scrollHeight <= this.nav.clientHeight) return;

        const navRect = this.nav.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        if (linkRect.top < navRect.top || linkRect.bottom > navRect.bottom) {
          this.nav.scrollTop += linkRect.top - navRect.top - this.nav.clientHeight / 2 + linkRect.height / 2;
        }
      }

      getLevel(heading) {
        return parseInt(heading.tagName.slice(1), 10);
      }

      slugify(text) {
        const slug = text
          .trim()
          .toLowerCase()
          .normalize('NFKD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^\p{L}\p{N}]+/gu, '-')
          .replace(/^-+|-+$/g, '');
        return slug || 'section';
      }

      uniqueId(base, usedIds) {
        let id = base;
        let index = 2;
        while (usedIds.has(id)) id = `${base}-${index++}`;
        usedIds.add(id);
        return id;
      }
    }
  );
}
